import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gauge, ShieldCheck, Cpu } from "lucide-react";

const PHOTOS = [
  {
    src: "/img/production/prod-still-2.webp",
    alt: "Electrical test stations checking every CORE 500 unit",
    callout: "Precision testing",
    sub: "12-station electrical & heat-cycle rig",
    icon: Gauge,
  },
  {
    src: "/img/production/prod-still-3.webp",
    alt: "Finished ceramic soleplates racked for inspection",
    callout: "Heat-plate verification",
    sub: "Every soleplate inspected for even heat",
    icon: ShieldCheck,
  },
  {
    src: "/img/production/prod-still-1.webp",
    alt: "Overview of the SunPress assembly line",
    callout: "Engineered from the ground up",
    sub: "500W element, thermostat & plate matched by hand",
    icon: Cpu,
  },
];

const PhotoCard = ({ p, className = "" }) => {
  const I = p.icon;
  return (
    <figure className={`relative overflow-hidden rounded-2xl ${className}`} style={{ border: "1px solid rgba(252,246,234,0.12)" }}>
      <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover" />
      <figcaption
        className="absolute inset-x-0 bottom-0 p-4 flex items-start gap-3"
        style={{ background: "linear-gradient(180deg, transparent, rgba(20,16,12,0.88))" }}
      >
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 mt-0.5" style={{ background: "var(--sun-orange)" }}>
          <I size={15} color="#1F1A14" strokeWidth={2.2} />
        </span>
        <span>
          <span className="block text-sm font-medium" style={{ color: "#FCF6EA" }}>{p.callout}</span>
          <span className="block text-xs mt-0.5" style={{ color: "rgba(252,246,234,0.72)" }}>{p.sub}</span>
        </span>
      </figcaption>
    </figure>
  );
};

export default function FounderStorySection() {
  return (
    <section data-testid="founder-story" className="relative overflow-hidden py-20 md:py-28" style={{ background: "var(--ink)", color: "#FCF6EA" }}>
      <div className="sunset-ambient" style={{ top: "-260px", left: "-200px", opacity: 0.35 }} />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center relative z-10">
        <div className="md:col-span-6">
          <div className="eyebrow" style={{ color: "var(--sun-amber)" }}>Why We Built SunPress Solar</div>
          <h2 className="display mt-4" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "#FCF6EA" }}>
            The iron that <span className="sunset-text">stopped fighting</span> the inverter.
          </h2>
          <div className="mt-8 space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(252,246,234,0.82)" }}>
            <p>
              Here is the problem every solar and inverter home in Nigeria knows too well: a standard electric iron
              consumes <strong style={{ color: "#FCF6EA" }}>1,500W–2,400W</strong>. Plug one in and it drains an
              inverter battery in minutes — or instantly trips the home solar system you spent so much to install.
              So families keep a fuel generator just to press clothes, and tailors lose working hours every time the grid blinks.
            </p>
            <p>
              We refused to accept that. Instead of tweaking an existing iron, we{" "}
              <strong style={{ color: "#FCF6EA" }}>custom-engineered the CORE 500 from the ground up</strong>:
              a 500W heating element positioned for even heat, a thermostat that holds its setting instead of
              wasting watts, and a ceramic soleplate verified unit by unit. The result presses fabric crisp —
              Ankara, cotton, native wear — without draining your inverter or needing a massive generator.
            </p>
            <p>
              Every photo you see here is from our own production line. We publish it because when you buy an
              appliance for an off-grid home, you deserve to see exactly how it was made.
            </p>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <img src="/img/flo-avatar-sm.webp" alt="Flo, founder of SunPress Solar" className="w-12 h-12 rounded-full object-cover" style={{ border: "2px solid var(--sun-orange)" }} />
            <div>
              <div className="font-serif text-lg" style={{ color: "#FCF6EA" }}>Flo</div>
              <div className="text-xs uppercase tracking-widest font-mono" style={{ color: "rgba(252,246,234,0.6)" }}>Founder, SunPress Solar</div>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/products/CORE-500" data-testid="founder-story-cta" className="btn-brand justify-center">
              Pre-order the CORE 500 <ArrowRight size={16} />
            </Link>
            <Link to="/about" data-testid="founder-story-about" className="btn-outline justify-center" style={{ color: "#FCF6EA", borderColor: "rgba(252,246,234,0.3)" }}>
              Read the full story
            </Link>
          </div>
        </div>

        <div className="md:col-span-6 grid grid-cols-2 gap-4">
          <PhotoCard p={PHOTOS[0]} className="col-span-2" />
          <PhotoCard p={PHOTOS[1]} className="aspect-[4/5]" />
          <PhotoCard p={PHOTOS[2]} className="aspect-[4/5]" />
        </div>
      </div>
    </section>
  );
}
